import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {number} */
  #lastChangedFieldsetIndex = -1;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    if (event.target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(event.target.dataset.fieldsetIndex || '');
      this.#lastChangedFieldsetIndex = Number.isNaN(fieldsetIndex) ? -1 : fieldsetIndex;
    } else {
      this.#lastChangedFieldsetIndex = -1;
    }

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const optionValueId = selectedOption.dataset.optionValueId ?? '';
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector, optionValueId);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    // Phone cases use a native <select> for Phone Model. Read every current
    // control directly at request time so stale [selected] attributes cannot
    // produce a false unavailable combination.
    if (this.dataset.isPhoneCase === 'true' && !source) {
      const liveOptionValueIds = [];
      for (const control of this.querySelectorAll('.variant-picker__form select, .variant-picker__form fieldset')) {
        let selectedControl;
        if (control instanceof HTMLSelectElement) {
          selectedControl = control.selectedOptions[0];
        } else {
          selectedControl = control.querySelector('input[type="radio"]:checked');
        }
        const optionValueId = selectedControl?.dataset?.optionValueId;
        if (optionValueId) liveOptionValueIds.push(optionValueId);
      }
      if (liveOptionValueIds.length) {
        params.push(`option_values=${liveOptionValueIds.join(',')}`);
      }
    } else if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Builds one corrected request when a Ceramic Vase style change leaves a
   * dependent option (Size) on an unavailable combination.
   *
   * The first server response already contains the correct availability state
   * for the newly selected style, so it is the authoritative source for the
   * compatible dependent option. Products with a real sold-out variant keep
   * their variant ID and are not handled here.
   *
   * @param {Document} html - The first server response.
   * @param {string} requestUrl - The original request URL.
   * @returns {string | null} A corrected request URL, or null when no fallback is needed.
   */
  #buildDependentOptionFallbackUrl(html, requestUrl) {
    if (this.dataset.productType !== 'Personalized Ceramic Vase' || this.#lastChangedFieldsetIndex < 0) {
      return null;
    }

    const matchingPicker = Array.from(html.querySelectorAll('variant-picker')).find(
      (picker) => picker instanceof HTMLElement && picker.dataset.productId === this.dataset.productId
    );

    if (!(matchingPicker instanceof HTMLElement)) return null;

    const fieldsets = Array.from(matchingPicker.querySelectorAll('fieldset[data-fieldset-index]'));
    const dependentFieldset = fieldsets.find((fieldset) => {
      const fieldsetIndex = Number.parseInt(fieldset.dataset.fieldsetIndex || '');
      if (Number.isNaN(fieldsetIndex) || fieldsetIndex <= this.#lastChangedFieldsetIndex) return false;

      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      return (
        checkedInput instanceof HTMLInputElement &&
        (checkedInput.disabled || checkedInput.dataset.optionAvailable === 'false')
      );
    });

    if (!(dependentFieldset instanceof HTMLFieldSetElement)) return null;

    const fallbackInput = dependentFieldset.querySelector(
      'input[type="radio"]:not(:disabled)[data-option-available="true"][data-variant-id]'
    );

    if (!(fallbackInput instanceof HTMLInputElement)) return null;

    const selectedOptionValueIds = fieldsets.map((fieldset) => {
      const selectedInput = fieldset === dependentFieldset
        ? fallbackInput
        : fieldset.querySelector('input[type="radio"]:checked');

      return selectedInput instanceof HTMLInputElement ? selectedInput.dataset.optionValueId : undefined;
    });

    if (selectedOptionValueIds.some((optionValueId) => !optionValueId)) return null;

    const fallbackUrl = new URL(requestUrl, window.location.origin);
    fallbackUrl.searchParams.set('option_values', selectedOptionValueIds.join(','));

    return fallbackUrl.toString();
  }

  /**
   * Builds one corrected request for phone-case products when Shopify returns
   * null for a combination that can be resolved safely from the server-rendered
   * picker. This never enables ATC by itself: it only retries when every selected
   * option can be mapped to an available option value with a concrete variant ID.
   *
   * @param {Document} html
   * @param {string} requestUrl
   * @returns {string | null}
   */
  #buildPhoneCaseFallbackUrl(html, requestUrl) {
    if (this.dataset.productType || !this.dataset.productId) {
      // productType is intentionally not used for matching; phone cases are tag-driven in Liquid.
    }

    const currentPicker = this;
    const serverPicker = Array.from(html.querySelectorAll('variant-picker')).find(
      (picker) => picker instanceof HTMLElement && picker.dataset.productId === this.dataset.productId
    );
    if (!(serverPicker instanceof HTMLElement)) return null;

    const currentControls = Array.from(
      currentPicker.querySelectorAll('.variant-picker__form select, .variant-picker__form fieldset')
    );
    if (currentControls.length < 2) return null;

    const selectedNamesAndValues = currentControls.map((control) => {
      if (control instanceof HTMLSelectElement) {
        return { name: control.name.replace(/^options\[|\]$/g, ''), value: control.value };
      }
      const checked = control.querySelector('input[type="radio"]:checked');
      return checked instanceof HTMLInputElement
        ? { name: checked.dataset.optionName || '', value: checked.value }
        : null;
    });
    if (selectedNamesAndValues.some((item) => !item?.name || !item?.value)) return null;

    const serverControls = Array.from(
      serverPicker.querySelectorAll('.variant-picker__form select, .variant-picker__form fieldset')
    );
    const ids = [];

    for (const selected of selectedNamesAndValues) {
      const control = serverControls.find((candidate) => {
        if (candidate instanceof HTMLSelectElement) {
          return candidate.name.replace(/^options\[|\]$/g, '') === selected.name;
        }
        const input = candidate.querySelector('input[data-option-name]');
        return input instanceof HTMLInputElement && input.dataset.optionName === selected.name;
      });
      if (!control) return null;

      let option;
      if (control instanceof HTMLSelectElement) {
        option = Array.from(control.options).find((item) => item.value === selected.value);
      } else {
        option = Array.from(control.querySelectorAll('input[type="radio"]')).find(
          (item) => item instanceof HTMLInputElement && item.value === selected.value
        );
      }
      if (!(option instanceof HTMLElement)) return null;
      if (option.dataset.optionAvailable === 'false') return null;
      if (!option.dataset.optionValueId) return null;
      ids.push(option.dataset.optionValueId);
    }

    const fallbackUrl = new URL(requestUrl, window.location.origin);
    fallbackUrl.searchParams.set('option_values', ids.join(','));
    return fallbackUrl.toString();
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {string} [optionValueId] - The selected option value ID for event detail.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, optionValueId = '') {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: abortController.signal })
      .then((response) => response.text())
      .then(async (responseText) => {
        this.#pendingRequestUrl = undefined;
        let html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        let variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        let textContent = variantPickerJsonScript?.textContent;
        let fallbackApplied = false;

        if (textContent && JSON.parse(textContent) == null) {
          let fallbackRequestUrl = this.#buildDependentOptionFallbackUrl(html, requestUrl);

          if (!fallbackRequestUrl && this.querySelector('select[name="options[Phone Model]"]')) {
            fallbackRequestUrl = this.#buildPhoneCaseFallbackUrl(html, requestUrl);
          }

          if (fallbackRequestUrl) {
            const fallbackResponse = await fetch(fallbackRequestUrl, { signal: abortController.signal });
            responseText = await fallbackResponse.text();
            html = new DOMParser().parseFromString(responseText, 'text/html');
            html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
            variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
            textContent = variantPickerJsonScript?.textContent;
            fallbackApplied = true;
          }
        }

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            if (
              fallbackApplied &&
              variantData.id &&
              this.dataset.templateProductMatch === 'true' &&
              !this.closest('product-card') &&
              !this.closest('quick-add-dialog')
            ) {
              const url = new URL(window.location.href);
              url.searchParams.set('variant', String(variantData.id));

              if (url.href !== window.location.href) {
                yieldToMainThread().then(() => {
                  history.replaceState({}, '', url.toString());
                });
              }
            }

            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLSelectElement && this.contains(activeElement)) {
      const selected = activeElement.selectedOptions[0];
      if (selected instanceof HTMLOptionElement) return selected;
    }

    const checkedInput = this.querySelector('fieldset input:checked');
    if (checkedInput instanceof HTMLInputElement) return checkedInput;

    const select = this.querySelector('select');
    const selected = select instanceof HTMLSelectElement ? select.selectedOptions[0] : undefined;
    return selected instanceof HTMLOptionElement ? selected : undefined;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type {(HTMLOptionElement | HTMLInputElement)[]} */
    const selectedOptions = [];

    for (const select of this.querySelectorAll('select')) {
      if (select instanceof HTMLSelectElement) {
        const selected = select.selectedOptions[0];
        if (selected instanceof HTMLOptionElement) selectedOptions.push(selected);
      }
    }

    for (const input of this.querySelectorAll('fieldset input:checked')) {
      if (input instanceof HTMLInputElement) selectedOptions.push(input);
    }

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
